import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, MapPinIcon, ZapIcon, TrendingUpIcon, BarChart3Icon, UsersIcon, MessageSquareIcon, PhoneIcon, GlobeIcon, CheckCircleIcon, MousePointerClickIcon } from 'lucide-react';
import { ScrollReveal } from '../../components/ScrollReveal';
import { SEOHead } from '../../components/SEOHead';
const faqs = [{
  question: 'How did Viridia help Finish Coat Painting increase revenue?',
  answer: "Using Viridia's all-in-one system, we implemented AI-powered messaging to handle inbound Yelp conversations instantly. In 30 days, the AI booked 29 estimates, generating $15.05k in closed-won revenue from end-to-end AI bookings alone - not including revenue from phone calls or website leads."
}, {
  question: 'Can AI follow-up actually close jobs for contractors?',
  answer: 'Yes. The Finish Coat Painting case study proves that AI follow-up does more than just respond fast - it helps close real jobs. By engaging high-intent local demand immediately, AI turns inquiries into booked estimates and closed revenue.'
}];
export function FinishCoatPaintingPage() {
  return <div className="bg-viridia-black min-h-screen pt-24" data-id="element-1761" data-mp="lxxogizjjyaq">
      <SEOHead title="Finish Coat Painting Case Study | $15.05k AI Revenue | Viridia Analytics" description="See how Finish Coat Painting worked with Viridia Analytics to get 29 AI-booked estimates and $15.05k in closed-won revenue in 30 days." canonical="/case-studies/finish-coat-painting" keywords="painting marketing case study, Yelp ads for painters, AI lead conversion, Finish Coat Painting" faq={faqs} data-id="element-1762" data-mp="lziga0uh134w" />

      <section className="py-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-1763" data-mp="zx751jrnsuf8">
        <ScrollReveal data-id="element-1764" data-mp="zvvwl6n9qyhs">
          <div className="mb-6" data-id="element-1765" data-mp="36nn2itsf7g2d">
            <Link to="/case-studies" className="inline-flex items-center text-gray-400 hover:text-white transition-colors text-sm font-medium" data-id="element-1766" data-mp="36oy7uy1ikvvl">
              <ArrowRightIcon className="w-4 h-4 mr-2 rotate-180" data-id="element-1767" data-mp="15mywue8m4n0e" />
              Back to Case Studies
            </Link>
          </div>

          <div className="bg-viridia-surface border border-gray-800 rounded-2xl overflow-hidden shadow-2xl" data-id="element-1768" data-mp="1an4j1ew45hb3">
            {/* Header / Snapshot */}
            <div className="bg-gray-900 p-8 lg:p-12 relative overflow-hidden border-b border-gray-800" data-id="element-1769" data-mp="1jlylvn01f3x3">
              <div className="absolute top-0 right-0 w-96 h-96 bg-orange-500/10 rounded-full blur-[100px]" data-id="element-1770" data-mp="1olkvznkkk41o"></div>
              <div className="relative z-10" data-id="element-1771" data-mp="1til5k8gmnoxj">
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-orange-500/10 text-orange-400 text-xs font-bold tracking-wide uppercase mb-6 border border-orange-500/20" data-id="element-1772" data-mp="1yamebkel6xit">
                  Painting Success Story
                </div>
                <h1 className="text-4xl lg:text-5xl font-bold text-white mb-8 leading-tight max-w-4xl" data-id="element-1773" data-mp="27clu69dv7vk6">
                  Finish Coat Painting: 29 AI-Booked Estimates and $15.05k in
                  Closed Revenue in 30 Days
                </h1>

                {/* Snapshot Pills */}
                <div className="flex flex-wrap gap-3 mb-10" data-id="element-1774" data-mp="3rsa7c6s4be5r">
                  <div className="inline-flex items-center px-3 py-1.5 rounded-lg bg-viridia-black border border-gray-700 text-gray-300 text-sm" data-id="element-1775" data-mp="3nrqdpd6r6sod">
                    <MapPinIcon className="w-4 h-4 mr-2 text-orange-500" data-id="element-1776" data-mp="3ityj2beq4sgh" />
                    Residential/Commercial Painting
                  </div>
                  <div className="inline-flex items-center px-3 py-1.5 rounded-lg bg-viridia-black border border-gray-700 text-gray-300 text-sm" data-id="element-1777" data-mp="fbwtan9awx21">
                    <ZapIcon className="w-4 h-4 mr-2 text-orange-500" data-id="element-1778" data-mp="ajwzzdnql2ef" />
                    All-in-One System
                  </div>
                </div>

                {/* Hero Stats Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6" data-id="element-1779" data-mp="132kda6whrd4y">
                  <div className="bg-viridia-black/50 p-6 rounded-xl border border-gray-800" data-id="element-1780" data-mp="yaivk5gk0u7j">
                    <p className="text-3xl sm:text-4xl font-bold text-orange-500 mb-2" data-id="element-1781" data-mp="hurtxobo41lo">
                      29
                    </p>
                    <p className="text-white font-medium text-sm" data-id="element-1782" data-mp="mvxqjx8g890h">
                      AI-Booked Estimates
                    </p>
                  </div>
                  <div className="bg-viridia-black/50 p-6 rounded-xl border border-gray-800" data-id="element-1783" data-mp="dt6b7mcgct3k">
                    <p className="text-3xl sm:text-4xl font-bold text-orange-500 mb-2" data-id="element-1784" data-mp="11jiprg27do34">
                      $15.05k
                    </p>
                    <p className="text-white font-medium text-sm" data-id="element-1785" data-mp="15kchylmohp1t">
                      Closed-Won Revenue
                    </p>
                  </div>
                  <div className="bg-viridia-black/50 p-6 rounded-xl border border-gray-800" data-id="element-1786" data-mp="wsagck0vi2py">
                    <p className="text-3xl sm:text-4xl font-bold text-orange-500 mb-2" data-id="element-1787" data-mp="1kiel4upss8ij">
                      138
                    </p>
                    <p className="text-white font-medium text-sm" data-id="element-1788" data-mp="1p9mxnzmfc9zx">
                      Total Leads
                    </p>
                  </div>
                  <div className="bg-viridia-black/50 p-6 rounded-xl border border-gray-800" data-id="element-1789" data-mp="1um1rifcxom0i">
                    <p className="text-3xl sm:text-4xl font-bold text-orange-500 mb-2" data-id="element-1790" data-mp="1uk8rhiwtt01h">
                      64%
                    </p>
                    <p className="text-white font-medium text-sm" data-id="element-1791" data-mp="23e3ou4rphm6k">
                      Lead Growth
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Challenge / Solution */}
            <div className="p-8 lg:p-12 bg-viridia-black border-b border-gray-800" data-id="element-1792" data-mp="1bpadakc90x3b">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12" data-id="element-1793" data-mp="1poa0dnk453xd">
                <div data-id="element-1794" data-mp="1kn463lat18lq">
                  <div className="w-12 h-12 rounded-xl bg-red-500/10 flex items-center justify-center mb-6" data-id="element-1795" data-mp="1kmkn019f4e64">
                    <TrendingUpIcon className="w-6 h-6 text-red-500" data-id="element-1796" data-mp="3148bry2rk21r" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3" data-id="element-1797" data-mp="2w559gle649xh">
                    The Challenge
                  </h3>
                  <p className="text-gray-400 leading-relaxed" data-id="element-1798" data-mp="38525zrxknbom">
                    Finish Coat Painting was generating Yelp demand but couldn't
                    keep up with inbound conversations. Leads were slipping
                    through the cracks because the team was busy on job sites,
                    and follow-up was inconsistent.
                  </p>
                </div>
                <div data-id="element-1799" data-mp="2peu9vtsahlhx">
                  <div className="w-12 h-12 rounded-xl bg-orange-500/10 flex items-center justify-center mb-6" data-id="element-1800" data-mp="2pe2n98go6y3s">
                    <BarChart3Icon className="w-6 h-6 text-orange-500" data-id="element-1801" data-mp="2ucw0r0vyip3y" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3" data-id="element-1802" data-mp="2z5p6ns35w3hm">
                    The Viridia Solution
                  </h3>
                  <p className="text-gray-400 leading-relaxed" data-id="element-1803" data-mp="21ozx1c7yzwxo">
                    We deployed the full Viridia system - Yelp Ads
                    management with GEO targeting, AI-powered messaging to
                    handle inbound conversations instantly, and automated
                    estimate booking so every inquiry gets a response in
                    seconds.
                  </p>
                </div>
              </div>
            </div>

            {/* AI Bookings & Revenue */}
            <div className="grid grid-cols-1 md:grid-cols-2 border-b border-gray-800" data-id="element-1804" data-mp="1k9nlid4m3zlo">
              <div className="p-8 lg:p-12 bg-viridia-surface border-r border-gray-800" data-id="element-1805" data-mp="1g7iwxst0tt5w">
                <div className="w-12 h-12 rounded-xl bg-orange-500/10 flex items-center justify-center mb-6" data-id="element-1806" data-mp="1behejatlvbhk">
                  <ZapIcon className="w-6 h-6 text-orange-500" data-id="element-1807" data-mp="16gftwlgj743z" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4" data-id="element-1808" data-mp="11ieecaibdmx9">
                  29 AI-Booked Estimates
                </h3>
                <p className="text-gray-400 leading-relaxed mb-6" data-id="element-1809" data-mp="wk343yo3xaei">
                  In 30 days, the AI booked 29 estimates (roughly one per day).
                  One booking actually represented 2 separate guys getting
                  quotes. These were{' '}
                  <strong className="text-white" data-id="element-1810" data-mp="2lauyihp5jwp3">AI-worked leads</strong>, not
                  random CRM leads. The AI handled the conversation, qualified
                  the prospect, and booked the estimate.
                </p>
              </div>
              <div className="p-8 lg:p-12 bg-viridia-surface" data-id="element-1811" data-mp="1xmlcpfrxv8xy">
                <div className="w-12 h-12 rounded-xl bg-orange-500/10 flex items-center justify-center mb-6" data-id="element-1812" data-mp="1xlab9mlyuda0">
                  <UsersIcon className="w-6 h-6 text-orange-500" data-id="element-1813" data-mp="22felqfufx3fu" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4" data-id="element-1814" data-mp="1nq8m4qroxb7f">
                  $15.05k Closed-Won Revenue
                </h3>
                <p className="text-gray-400 leading-relaxed mb-6" data-id="element-1815" data-mp="1enppwpietwu5">
                  The 29 AI-booked estimates resulted in $15.05k in closed-won
                  revenue. This revenue came{' '}
                  <strong className="text-white" data-id="element-1816" data-mp="1010qsos2lloa">
                    from end-to-end AI bookings alone
                  </strong>{' '}
                  - it does NOT include revenue from phone calls or website
                  leads. This proves that AI follow-up doesn't just respond fast
                  - it helps close real jobs.
                </p>
              </div>
            </div>

            {/* 30-Day Performance Breakdown */}
            <div className="p-8 lg:p-12 bg-viridia-black border-b border-gray-800" data-id="element-1817" data-mp="1iyle3rf6sc6s">
              <h3 className="text-2xl font-bold text-white mb-8" data-id="element-1818" data-mp="1e1l5b8qycvbz">
                30-Day Performance
              </h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12" data-id="element-1819" data-mp="1rqwv6moeuna3">
                <div className="space-y-6" data-id="element-1820" data-mp="1nqcuj94mp1j7">
                  <div className="bg-viridia-surface p-6 rounded-xl border border-gray-800" data-id="element-1821" data-mp="1nregi8n6n8fp">
                    <div className="flex justify-between items-center mb-2" data-id="element-1822" data-mp="21qnoil18iw8l">
                      <span className="text-gray-400" data-id="element-1823" data-mp="1wsm49ws2oqvj">Impressions</span>
                      <span className="text-white font-bold" data-id="element-1824" data-mp="y3m5qzoamgw4">33.2k</span>
                    </div>
                    <div className="w-full bg-gray-800 rounded-full h-2 mb-2" data-id="element-1825" data-mp="tbua4aqa5kz4">
                      <div className="bg-orange-500 h-2 rounded-full" style={{
                      width: '89%'
                    }} data-id="element-1826" data-mp="pasb9v09zaxo"></div>
                    </div>
                    <p className="text-xs text-gray-500" data-id="element-1827" data-mp="1h0nd2r5bkt38">
                      29.4k (89%) from Yelp Ads
                    </p>
                  </div>
                  <div className="bg-viridia-surface p-6 rounded-xl border border-gray-800" data-id="element-1828" data-mp="181a37xdaa2hz">
                    <div className="flex justify-between items-center mb-2" data-id="element-1829" data-mp="1325t94ijc0ql">
                      <span className="text-gray-400" data-id="element-1830" data-mp="1349ufep1uln6">Page Visits</span>
                      <span className="text-white font-bold" data-id="element-1831" data-mp="3ssafztkr41tv">370</span>
                    </div>
                    <div className="w-full bg-gray-800 rounded-full h-2 mb-2" data-id="element-1832" data-mp="3nrmsvizn854x">
                      <div className="bg-orange-500 h-2 rounded-full" style={{
                      width: '79%'
                    }} data-id="element-1833" data-mp="3dp91h57o493q"></div>
                    </div>
                    <p className="text-xs text-gray-500" data-id="element-1834" data-mp="3nkkglomznic0">
                      293 (79%) from Yelp Ads
                    </p>
                  </div>
                  <div className="bg-viridia-surface p-6 rounded-xl border border-gray-800" data-id="element-1835" data-mp="acohubf9zqb">
                    <div className="flex justify-between items-center mb-2" data-id="element-1836" data-mp="584prexyiy76">
                      <span className="text-gray-400" data-id="element-1837" data-mp="a7r3dphp6ggi">Total Leads</span>
                      <span className="text-white font-bold" data-id="element-1838" data-mp="a5o6qmm1hp2t">138</span>
                    </div>
                    <div className="w-full bg-gray-800 rounded-full h-2 mb-2" data-id="element-1839" data-mp="f62d807zsqyq">
                      <div className="bg-orange-500 h-2 rounded-full" style={{
                      width: '59%'
                    }} data-id="element-1840" data-mp="2c0f9q7pn1k0i"></div>
                    </div>
                    <p className="text-xs text-gray-500" data-id="element-1841" data-mp="23yfvrl5i263s">
                      81 (59%) from Yelp Ads
                    </p>
                  </div>
                </div>

                <div data-id="element-1842" data-mp="2itlzg1h4hsty">
                  <h4 className="text-lg font-semibold text-white mb-4" data-id="element-1843" data-mp="2dwlvbw3u305h">
                    Lead Mix
                  </h4>
                  <div className="bg-viridia-surface rounded-xl border border-gray-800 overflow-hidden" data-id="element-1844" data-mp="2rvliezbp76zj">
                    <table className="w-full text-left text-sm" data-id="element-1845" data-mp="2my21tsrj2i4f">
                      <thead className="bg-gray-900 border-b border-gray-800" data-id="element-1846" data-mp="31m6o16pshbry">
                        <tr data-id="element-1847" data-mp="31o9bbizk4qsz">
                          <th className="p-4 text-gray-400 font-medium" data-id="element-1848" data-mp="2wndcvkxz5wuy">
                            Lead Type
                          </th>
                          <th className="p-4 text-gray-400 font-medium" data-id="element-1849" data-mp="3anebetqros79">
                            Count
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-800" data-id="element-1850" data-mp="3pav4y1yywv1t">
                        <tr data-id="element-1851" data-mp="3u96dm9dj0zig">
                          <td className="p-4 text-white flex items-center" data-id="element-1852" data-mp="3u7d9b0qdyrdm">
                            <PhoneIcon className="w-4 h-4 mr-2 text-orange-500" data-id="element-1853" data-mp="3g9ok5gpacy2r" />{' '}
                            Calls
                          </td>
                          <td className="p-4 text-white font-bold" data-id="element-1854" data-mp="3la2ux6i9um4h">52</td>
                        </tr>
                        <tr data-id="element-1855" data-mp="36gzfp5s5kkgx">
                          <td className="p-4 text-white flex items-center" data-id="element-1856" data-mp="3bac013psu53v">
                            <MessageSquareIcon className="w-4 h-4 mr-2 text-orange-500" data-id="element-1857" data-mp="3bbn0atk1cuq8" />{' '}
                            Messages
                          </td>
                          <td className="p-4 text-white font-bold" data-id="element-1858" data-mp="31kiog2pnppzm">50</td>
                        </tr>
                        <tr data-id="element-1859" data-mp="2nkq0gf5ilnvc">
                          <td className="p-4 text-white flex items-center" data-id="element-1860" data-mp="2nmkd766mu9oc">
                            <GlobeIcon className="w-4 h-4 mr-2 text-orange-500" data-id="element-1861" data-mp="2sij6972wofv3" />{' '}
                            Website Visits
                          </td>
                          <td className="p-4 text-white font-bold" data-id="element-1862" data-mp="3quvj9cd12851">33</td>
                        </tr>
                        <tr data-id="element-1863" data-mp="3m3ltmbrhb4t5">
                          <td className="p-4 text-white flex items-center" data-id="element-1864" data-mp="3m2u6lpby6feh">
                            <MousePointerClickIcon className="w-4 h-4 mr-2 text-orange-500" data-id="element-1865" data-mp="3h49ao23qahvu" />{' '}
                            CTA Clicks
                          </td>
                          <td className="p-4 text-white font-bold" data-id="element-1866" data-mp="377e91ac5s1dc">3</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            {/* Local Visibility */}
            <div className="p-8 lg:p-12 bg-viridia-surface" data-id="element-1867" data-mp="2y8svxupghejy">
              <div className="w-12 h-12 rounded-xl bg-orange-500/10 flex items-center justify-center mb-6" data-id="element-1868" data-mp="11cv620x8f8lw">
                <MapPinIcon className="w-6 h-6 text-orange-500" data-id="element-1869" data-mp="wfmitlx88eds" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4" data-id="element-1870" data-mp="13xt4fg3piqr5">
                GEO & Local Painting Demand
              </h3>
              <ul className="space-y-4 mb-8" data-id="element-1871" data-mp="18vm924pd3cpb">
                <li className="flex items-start" data-id="element-1872" data-mp="1dnvxtheqq6q0">
                  <CheckCircleIcon className="w-5 h-5 text-orange-500 mr-3 flex-shrink-0 mt-0.5" data-id="element-1873" data-mp="1dqidy5fp5uju" />
                  <span className="text-gray-300" data-id="element-1874" data-mp="1ipl82uu52gd1">
                    Finish Coat received more page views than similar businesses
                    on Yelp.
                  </span>
                </li>
                <li className="flex items-start" data-id="element-1875" data-mp="q0xjapa6l8uq">
                  <CheckCircleIcon className="w-5 h-5 text-orange-500 mr-3 flex-shrink-0 mt-0.5" data-id="element-1876" data-mp="uxo57t8o2yo2" />
                  <span className="text-gray-300" data-id="element-1877" data-mp="yzrjepmq0hd1">
                    Captured rising local search trends for 'Painters Interior',
                    'House Painters', and 'Interior Painting'.
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </ScrollReveal>

        <ScrollReveal delay={0.2} data-id="element-1878" data-mp="b8lk43el5a4f">
          <div className="mt-12 text-center" data-id="element-1879" data-mp="bbradlyyai1q">
            <h3 className="text-2xl font-bold text-white mb-6" data-id="element-1880" data-mp="6bmv1z9s3td3">
              Want to see what's leaking leads in your pipeline?
            </h3>
            <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 text-base font-bold text-viridia-black bg-white rounded-lg hover:bg-gray-200 transition-all duration-300" data-id="element-1881" data-mp="k8id2h80cv5a">
              Book a Strategy Call
              <ArrowRightIcon className="ml-2 w-5 h-5" data-id="element-1882" data-mp="3jrazc8sl9eaz" />
            </Link>
          </div>
        </ScrollReveal>
      </section>
    </div>;
}